import { useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { TOTAL_MIN, ROW_H, toMin, toHHMM } from './constants';


const GanttBar = ({ 
  flight, 
  totalW, 
  color, 
  isSelected, 
  onSelect, 
  onEditRequest, 
  onDragEnd 
}) => {
  const stdMin = toMin(flight.std);
  const blk    = flight.blockTime || 60;
  const left   = (stdMin / TOTAL_MIN) * totalW;
  const width  = Math.max(8, (blk / TOTAL_MIN) * totalW);

  const dragRef    = useRef(false);
  const isDownRef  = useRef(false);
  const startXRef  = useRef(0);
  const origStdRef = useRef(stdMin);
  const clickRef   = useRef(true);

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    isDownRef.current  = true;
    startXRef.current  = e.clientX;
    origStdRef.current = toMin(flight.std);
    dragRef.current    = false;
    clickRef.current   = true;
  };

  const handlePointerMove = (e) => {
    if (!isDownRef.current) return;
    const moved = Math.abs(e.clientX - startXRef.current) > 4;
    if (!moved) return;
    dragRef.current  = true;
    clickRef.current = false;
    const delta    = e.clientX - startXRef.current;
    const deltaMin = Math.round((delta / totalW) * TOTAL_MIN / 15) * 15;
    const newStd   = origStdRef.current + deltaMin;
    const newSta   = newStd + blk;
    e.currentTarget.style.left         = `${((((newStd % TOTAL_MIN) + TOTAL_MIN) % TOTAL_MIN) / TOTAL_MIN) * totalW}px`;
    e.currentTarget.dataset.newstd     = toHHMM(newStd);
    e.currentTarget.dataset.newsta     = toHHMM(newSta);
  };

  const handlePointerUp = (e) => {
    isDownRef.current = false;
    if (dragRef.current) {
      const newStd = e.currentTarget.dataset.newstd;
      const newSta = e.currentTarget.dataset.newsta;
      if (newStd && newStd !== flight.std) onDragEnd(flight._id, newStd, newSta);
    }
    dragRef.current = false;
  };

  const handleClick       = (e) => { e.stopPropagation(); if (clickRef.current) onSelect(flight); };
  const handleDoubleClick = (e) => { e.stopPropagation(); onEditRequest(flight); };

  const isPartial = (flight.daysOfOperation || '').length < 7;
  const showRoute = width > 50;
  const showTimes = width > 80;

  return (
    <Tooltip arrow title={
      `${flight.carrier}${flight.flightNo}  ${flight.dep}→${flight.arr}  ${flight.std}-${flight.sta}${flight.nextDay ? `+${flight.nextDay}` : ''}` +
      `  |  BLK ${Math.floor(blk / 60)}h${String(blk % 60).padStart(2, '0')}` +
      `  |  ${flight.daysOfOperation || '?'}  |  ${flight.acType || '—'}`
    }>
      <Box
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        sx={{
          position: 'absolute', left, top: 6, height: ROW_H - 12, width,
          bgcolor: color,
          backgroundImage: isPartial
            ? 'repeating-linear-gradient(135deg, rgba(0,0,0,0.1) 0px, rgba(0,0,0,0.1) 3px, transparent 3px, transparent 9px)'
            : undefined,
          borderRadius: '4px',
          cursor: 'grab',
          userSelect: 'none',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', px: 0.6,
          border: isSelected ? '2px solid rgba(255,255,255,0.9)' : '1px solid rgba(255,255,255,0.15)',
          boxShadow: isSelected ? `0 0 0 2px ${color}88, 0 2px 8px rgba(0,0,0,0.4)` : '0 1px 4px rgba(0,0,0,0.35)',
          transition: 'box-shadow 0.1s, filter 0.1s',
          filter: isSelected ? 'brightness(1.18)' : undefined,
          '&:hover': { filter: 'brightness(1.12)', zIndex: 4 },
          '&:active': { cursor: 'grabbing' },
          touchAction: 'none',
          zIndex: isSelected ? 3 : 2,
        }}
      >
        <Typography sx={{ fontSize: '0.63rem', color: '#fff', fontFamily: 'monospace', fontWeight: 'bold', lineHeight: 1.25, pointerEvents: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
          {flight.carrier}{flight.flightNo}
        </Typography>
        {showRoute && (
          <Typography sx={{ fontSize: '0.57rem', color: 'rgba(255,255,255,0.88)', fontFamily: 'monospace', lineHeight: 1.15, pointerEvents: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
            {flight.dep}→{flight.arr}
          </Typography>
        )}
        {showTimes && (
          <Typography sx={{ fontSize: '0.52rem', color: 'rgba(255,255,255,0.68)', fontFamily: 'monospace', lineHeight: 1.1, pointerEvents: 'none', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            {flight.std}-{flight.sta}{flight.nextDay ? `+${flight.nextDay}` : ''}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default GanttBar;
